// Halo cart drawer: fast, resilient, no loops
console.log('[TP] loaded');

const COLLECTION_HANDLE = 'singles';
const THRESHOLD = 10;
const CACHE_KEY = 'tp_singles_ids_v1';
const CACHE_TTL = 6 * 60 * 60 * 1000; // 6h
const PROGRESS_SELECTOR = '#tracks-progress';

let IDS_PROMISE = null;
let lastCartAt = 0;
let cartPromise = null;
let lastCart = null;
let lastSig = null; // qty|pct signature to prevent repeat updates

// ---------- IDs: preload + cache ----------
function loadIdsFromCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { when, ids } = JSON.parse(raw);
    if (!when || !ids || Date.now() - when > CACHE_TTL) return null;
    return new Set(ids);
  } catch { return null; }
}
function saveIdsToCache(set) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ when: Date.now(), ids: [...set] })); } catch {}
}
async function fetchCollectionIds(handle) {
  const out = new Set();
  let page = 1, limit = 250;
  while (true) {
    const res = await fetch(`/collections/${handle}/products.json?limit=${limit}&page=${page}`, { credentials: 'same-origin' });
    if (!res.ok) break;
    const data = await res.json();
    const products = Array.isArray(data.products) ? data.products : [];
    products.forEach(p => out.add(p.id));
    if (products.length < limit) break;
    page++;
  }
  return out;
}
function preloadIds() {
  const cached = loadIdsFromCache();
  if (cached) {
    console.log('[TP] using cached Singles IDs:', cached.size);
    IDS_PROMISE = Promise.resolve(cached);
  } else {
    IDS_PROMISE = fetchCollectionIds(COLLECTION_HANDLE).then(ids => {
      console.log('[TP] fetched Singles IDs:', ids.size);
      saveIdsToCache(ids);
      return ids;
    }).catch(err => {
      console.error('[TP] ID fetch failed', err);
      return new Set();
    });
  }
}
document.addEventListener('DOMContentLoaded', preloadIds);

// ---------- Cart fetch (single-flight + cache) ----------
async function getCart() {
  const now = Date.now();

  // If a request is in flight, return it
  if (cartPromise) return cartPromise;

  // If fetched very recently, serve cached cart
  if (lastCart && now - lastCartAt < 600) return lastCart;

  cartPromise = fetch('/cart.js', { credentials: 'same-origin' })
    .then(res => {
      lastCartAt = Date.now();
      if (!res.ok) throw new Error('cart fetch ' + res.status);
      return res.json();
    })
    .then(json => {
      lastCart = json;
      return json;
    })
    .catch(err => {
      console.error('[TP] cart error', err);
      // Fall back to last known cart to avoid blanks
      return lastCart;
    })
    .finally(() => {
      // small timeout before allowing another request to avoid storms
      setTimeout(() => { cartPromise = null; }, 50);
    });

  return cartPromise;
}

// ---------- Render ----------
async function renderTracksProgress(root) {
  const node = root.querySelector(PROGRESS_SELECTOR);
  if (!node) return;

  const textEl = node.querySelector('.tracks-progress__text');
  const fillEl = node.querySelector('.tracks-progress__fill');
  if (!textEl || !fillEl) return;

  // Ensure IDs are loading and show non-empty placeholder
  if (!IDS_PROMISE) { preloadIds && preloadIds(); }
  if (!textEl.textContent) textEl.textContent = 'Calculating your free track…';
  if (!fillEl.style.width) fillEl.style.width = '0%';
  node.hidden = false;

  let ids;
  try {
    ids = await IDS_PROMISE;
  } catch {
    setTimeout(() => renderTracksProgress(root), 150);
    return;
  }

  const cart = await getCart();
  if (!cart) {
    setTimeout(() => renderTracksProgress(root), 180);
    return;
  }

  const qty = (cart.items || []).reduce((sum, i) => ids.has(i.product_id) ? sum + i.quantity : sum, 0);

  if (!qty) {
    const visibleCount = root.querySelector('[data-cart-count]');
    if (visibleCount && parseInt(visibleCount.textContent, 10) > 0) {
      setTimeout(() => renderTracksProgress(root), 180);
      return;
    }
    // Nothing qualifying in cart
    lastSig = '0|0';
    node.hidden = true;
    return;
  }

  // Behaviour:
  // 0–9: "Add X more to make your next one free"
  // 10:  "Woo! You’ve earned a free track. The next one you add is free."
  // 11+: "Currently unlocked: N free tracks. Add X more to unlock another!"
  const cycle = THRESHOLD + 1; // 10 paid + 1 free after that
  let pct = 0;
  let msg = '';

  if (qty < THRESHOLD) {
    const toNext = THRESHOLD - qty;
    pct = Math.round((qty / THRESHOLD) * 100);
    msg = `Buy ${THRESHOLD} tracks and the 11th is free. Add ${toNext} more to  qualify.`;
  } else if (qty === THRESHOLD) {
    pct = 100;
    msg = `Congrats! You’ve earned a free track. The next one you add is free.`;
  } else {
  const cycle = THRESHOLD + 1;                // 11 total per unlock block
  const unlocked = Math.floor(qty / cycle);   // 11→1, 22→2, 33→3...
  const paidSince = qty % cycle;              // 0 right after a free, 10 when the next is free
  const toNextPaid = THRESHOLD - paidSince;   // how many paid to add before the next free
  pct = Math.round((paidSince / THRESHOLD) * 100);

  if (paidSince === THRESHOLD) {
    // e.g. qty = 21 → next (22nd) is free
    msg = `Currently unlocked: ${unlocked} free track${unlocked > 1 ? 's' : ''}. The next one you add is free.`;
    pct = 100;
  } else {
    msg = `Currently unlocked: ${unlocked} free track${unlocked > 1 ? 's' : ''}. Add ${toNextPaid} more to unlock another!`;
  }
}


  // Short-circuit if nothing changed (prevents loops)
  const sig = `${qty}|${pct}`;
  

  // Apply UI
  textEl.textContent = msg;
  fillEl.style.width = pct + '%';
  node.classList.toggle('tracks-progress--unlocked', qty >= THRESHOLD);
  node.hidden = false;

  // ARIA
  node.setAttribute('role', 'progressbar');
  node.setAttribute('aria-valuemin', '0');
  node.setAttribute('aria-valuemax', '100');
  node.setAttribute('aria-valuenow', String(pct));

  console.log('[TP] updated', { qty, pct });
}

// ---------- Halo drawer detection (targeted, no global loops) ----------
(function hookHaloCartDrawer() {
  const drawer = document.getElementById('halo-cart-sidebar');
  if (!drawer) {
    console.warn('[TP] #halo-cart-sidebar not found yet; retrying soon');
    document.addEventListener('DOMContentLoaded', () => setTimeout(hookHaloCartDrawer, 50));
    return;
  }

  const debounced = debounce(() => renderTracksProgress(drawer), 120);

  // Observe ONLY the drawer, and ignore mutations that originate from our widget
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      const t = m.target && m.target.nodeType === 1 ? m.target : (m.target && m.target.parentElement);
      if (t && t.closest && t.closest(PROGRESS_SELECTOR)) {
        // Mutation caused by our own bar; ignore
        return;
      }
    }
    debounced();
  });
  mo.observe(drawer, { childList: true, subtree: true });

  // First render if already open/filled
  if (drawer.querySelector(PROGRESS_SELECTOR)) debounced();

  // Open triggers (double-fire to beat races on mobile)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-cart-sidebar], .header__icon--cart, .cart-link')) {
      setTimeout(() => debounced(), 150);
      setTimeout(() => debounced(), 500);
    }
  });

  // Known cart events
  ['cart:updated', 'ajaxProduct:added', 'cart:refresh', 'lineItem:updated'].forEach(evt =>
    document.addEventListener(evt, () => debounced())
  );

  // Drawer interactions
  drawer.addEventListener('click', (e) => {
    if (e.target.closest('[data-plus-quantity-cart],[data-minus-quantity-cart],[data-cart-remove]')) {
      setTimeout(() => debounced(), 500);
    }
  });

  // Quantity input changes (mobile keyboards fire change)
  drawer.addEventListener('change', (e) => {
    if (e.target.matches('input.quantity,[name="quantity"]')) {
      setTimeout(() => debounced(), 500);
    }
  });

  // Touch taps on +/- on some themes
  drawer.addEventListener('touchend', (e) => {
    if (e.target.closest('[data-plus-quantity-cart],[data-minus-quantity-cart]')) {
      setTimeout(() => debounced(), 500);
    }
  }, { passive: true });

  console.log('[TP] hooked Halo drawer');
})();

// ---------- Utils ----------
function debounce(fn, wait) {
  let t;
  return function () {
    clearTimeout(t);
    t = setTimeout(fn, wait);
  };
}

// Expose manual trigger (handy while testing)
window.TracksProgressRender = () => {
  const drawer = document.getElementById('halo-cart-sidebar');
  if (drawer) renderTracksProgress(drawer);
};

// Trigger render after an item is added via the product page button
document.addEventListener('click', e => {
  const btn = e.target.closest('#product-add-to-cart');
  if (!btn) return;
  setTimeout(() => {
    window.TracksProgressRender && window.TracksProgressRender();
  }, 500); // adjust for your AJAX add speed
});
